import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Form,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Alert,
} from "reactstrap";
import { api } from "../../config";
import { fetchComAuth, fetchComAuthJson } from '../../common/api';
import { formatarNumeroPTBR } from '../../utils/formatacao'; // ajuste o caminho

const TelaPavilhaoFeira = ({ feiraId, editando }) => {
  const [pavilhoes, setPavilhoes] = useState([]);

  // Modal state
  const [modalAberto, setModalAberto] = useState(false);
  const [editandoId, setEditandoId] = useState(null);

  // Form fields
  const [nome, setNome] = useState("");
  const [cor, setCor] = useState("#3b82f6");
  const [metragem, setMetragem] = useState(0);
  const [descricao, setDescricao] = useState("");
  const [erro, setErro] = useState("");
  const [altura_minima, setAlturaMinima] = useState(0);
  const [altura_maxima, setAlturaMaxima] = useState(0);

  const carregarDados = async () => {
    if (!feiraId) return;
    try {
      const response = await fetchComAuth(`${api.API_URL}/feira/pavilhao_feira/${feiraId}`, {
        method: 'GET'
      });
      const data = await response.json();
      setPavilhoes(data.data || []); // Garante que seja um array
    } catch (err) {
      console.error("Erro ao carregar os pavilhões:", err);
      setErro("Não foi possível carregar os dados.");
    }
  };

  useEffect(() => {
    if (editando) {
      carregarDados();
    }
  }, [feiraId, editando]);


  const limparFormulario = () => {
    setEditandoId(null);
    setNome("");
    setCor("#3b82f6");
    setMetragem(0);
    setAlturaMinima(0);
    setAlturaMaxima(0);
    setDescricao("");
    setErro("");
  };

  const abrirNovo = () => {
    limparFormulario();
    setModalAberto(true);
  };

  const abrirEdicao = (pavilhao) => {
    setEditandoId(pavilhao.id);
    setNome(pavilhao.nome);
    setCor(pavilhao.cor);
    setMetragem(pavilhao.metragem || 0); 
    setAlturaMinima(pavilhao.altura_minima || 0); 
    setAlturaMaxima(pavilhao.altura_maxima || 0); 
    setDescricao(pavilhao.descricao || "");
    setErro("");
    setModalAberto(true);
  };

  const excluir = (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este Pavilhão?")) return;
    
    fetchComAuth(`${api.API_URL}/pavilhao_feira/${id}`, { method: 'DELETE' })
      .then(res => {
        if (!res.ok) throw new Error('Erro ao excluir');
        carregarDados(); // Recarrega a lista para atualizar a tela
      })
      .catch(err => {
        console.error('Erro ao excluir pavilhão:', err);
        setErro("Falha ao excluir o item.");
      });
  };

  const validar = () => {
    if (!nome.trim()) return "O nome do Pavilhão é obrigatório.";

    const nomeExiste = pavilhoes.some(
      (s) => s.nome.trim().toLowerCase() === nome.trim().toLowerCase() && s.id !== editandoId
    );
    if (nomeExiste) return "Já existe um Pavilhão com esse nome.";

    // Removida a validação de 'ordem' que não existe neste formulário
    if (!/^#[0-9A-Fa-f]{6}$/i.test(cor)) return "Informe uma cor válida em HEX (ex: #3366ff).";

    return "";
  };

  const handleSalvar = () => {
    const erroValidacao = validar();
    if (erroValidacao) { 
      setErro(erroValidacao); 
      return; 
    }

    const payload = {
      id: editandoId, // Enviado para o backend saber se é um update
      nome: nome.trim(),
      cor,
      metragem: Number(metragem),
      descricao: descricao.trim(),
      feira_id: feiraId,
      altura_minima: Number(altura_minima),
      altura_maxima: Number(altura_maxima)
    };

    const method = editandoId ? 'PUT' : 'POST';
    const endpoint = `${api.API_URL}/pavilhao_feira`;

    fetchComAuthJson(endpoint, {
      method: method,
      body: JSON.stringify(payload)
    })
    .then(res => {
      if (!res.ok) throw new Error('Erro ao salvar');
      return res.json();
    })
    .then(() => {
      carregarDados(); // Recarrega os dados após salvar
      setModalAberto(false);
      limparFormulario();
    })
    .catch(err => {
      console.error('Erro ao salvar pavilhão:', err);
      setErro("Não foi possível salvar os dados. Verifique se já não existe um item com este nome.");
    });
  };

  return (
    <div>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <Button color="success" onClick={abrirNovo}>
                <i className="ri-add-line align-bottom me-1"></i> Adicionar Pavilhão
              </Button>
            </CardHeader>
            <CardBody>
              {pavilhoes.length === 0 ? (
                <p className="text-muted">Nenhum Pavilhão cadastrado.</p>
              ) : (
                <div className="table-responsive">
                  <Table className="table align-middle table-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th>Nome</th>
                        <th>Altura min/max</th>
                        <th>Cor</th>
                        <th>Área (m²)</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pavilhoes.map((pavilhao) => (
                        <tr key={pavilhao.id}>
                          <td>{pavilhao.nome}</td>
                          <td>{pavilhao.altura_minima}-{pavilhao.altura_maxima}</td>
                          <td>
                            <span
                              style={{
                                display: "inline-block",
                                width: 16,
                                height: 16,
                                background: pavilhao.cor,
                                borderRadius: 4,
                                marginRight: 8,
                                verticalAlign: "middle",
                                border: "1px solid #e5e7eb",
                              }}
                            />
                            {pavilhao.cor}
                          </td>
                          <td>{formatarNumeroPTBR(pavilhao.metragem)}</td>
                          <td>
                            <Button color="warning" size="sm" className="me-2" onClick={() => abrirEdicao(pavilhao)}>
                              Editar
                            </Button>
                            <Button color="danger" size="sm" onClick={() => excluir(pavilhao.id)}>
                              Excluir
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={modalAberto} toggle={() => setModalAberto(!modalAberto)} centered>
        <ModalHeader toggle={() => setModalAberto(!modalAberto)}>
          {editandoId ? "Editar Pavilhão" : "Novo Pavilhão"}
        </ModalHeader>
        <ModalBody>
          <Form>
            {erro && <Alert color="danger">{erro}</Alert>}
            
            <FormGroup className="mb-3">
              <Label>Nome*</Label>
              <Input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Nome do pavilhão"
              />
            </FormGroup>
                
            {/* <FormGroup className="mb-3">
                <Label>Descrição</Label>
                <Input
                  type="text"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Descrição do pavilhão"
                />
            </FormGroup> */}
        
            <FormGroup className="mb-3">
                <Label>Área (m²)</Label>
                  <Input
                  type="number"
                  value={metragem}
                  onChange={(e) => setMetragem(e.target.value)}
                  placeholder="Área do pavilhão"
                />
            </FormGroup>

            <Row>
              <Col xl={6}>
                <FormGroup className="mb-3">
                    <Label>Altura mínima</Label>
                      <Input
                      type="number"
                      value={altura_minima}
                      onChange={(e) => setAlturaMinima(e.target.value)}
                      placeholder="Altura mínima"
                    />
                </FormGroup>
              </Col>
              <Col xl={6}>    
                <FormGroup className="mb-3">
                    <Label>Altura máxima</Label>
                      <Input
                      type="number"
                      value={altura_maxima}
                      onChange={(e) => setAlturaMaxima(e.target.value)}
                      placeholder="Altura máxima"
                    />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup className="mb-3">
                <Label>Cor</Label>
                <Input 
                  type="color" 
                  className="form-control form-control-color w-100" 
                  value={cor}
                  onChange={(e) => setCor(e.target.value)}
                />
            </FormGroup>

            <small className="text-muted">* Campos obrigatórios</small>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalAberto(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSalvar}>
            {editandoId ? "Atualizar" : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default TelaPavilhaoFeira;